<template>
  <VCardTitle class="flex justify-space-between items-center p-0 border-b border-divider">
    <div class="text-h4 p-8">{{ t('Filter patients') }}</div>
    <VBtn
      icon="close"
      variant="plain"
      class="button-small mr-2"
      size="small"
      :ripple="false"
      data-testid="btn-close"
      @click="emits('close-dialog')"
    />
  </VCardTitle>

  <VCardItem class="px-8 py-0 pt-4">
    <span class="text-subtitle-1">
      {{ t('Hospital') }}
    </span>
    <VAutocomplete
      v-model="hospital"
      clear-icon="custom:clear20"
      :items="hospitalData"
      :value-comparator="compareById"
      chips
      closable-chips
      multiple
      :placeholder="t('Please select...')"
    />
  </VCardItem>

  <VCardItem class="px-8 py-0">
    <span class="text-subtitle-1">
      {{ t('Departments') }}
    </span>
    <VAutocomplete
      v-model="departments"
      clear-icon="custom:clear20"
      :items="departmentDataWithPatientCount"
      :value-comparator="compareById"
      chips
      closable-chips
      multiple
      :placeholder="t('Please select...')"
    />
  </VCardItem>

  <VCardItem class="px-8 py-0">
    <span class="text-subtitle-1">
      {{ t('Wards') }}
    </span>
    <VAutocomplete
      v-model="wards"
      clear-icon="custom:clear20"
      :items="wardDataWithPatientCount"
      :value-comparator="compareById"
      chips
      closable-chips
      multiple
      :placeholder="t('Please select...')"
    />
  </VCardItem>

  <VCardItem class="px-8 py-0">
    <span class="text-subtitle-1">
      {{ t('Vital Signs') }}
    </span>

    <div class="flex gap-2 py-2 items-center flex-wrap">
      <VChip
        v-if="hasRangeFilter(temperatureRange)"
        closable
        @click:close="clearRange('temperatureRange')"
      >
        {{ getLabelWithDefaultUnit(Parameter.Temperature) }}: {{ formatRange(temperatureRange) }}
      </VChip>

      <VChip v-if="hasRangeFilter(lactateRange)" closable @click:close="clearRange('lactateRange')">
        {{ getLabelWithDefaultUnit(Parameter.Lactate) }}: {{ formatRange(lactateRange) }}
      </VChip>

      <VChip v-if="hasRangeFilter(pHRange)" closable @click:close="clearRange('pHRange')">
        {{ getParameterLabel(Parameter.PH) }}: {{ formatRange(pHRange) }}
      </VChip>

      <VChip
        v-if="hasRangeFilter(potassiumRange)"
        closable
        @click:close="clearRange('potassiumRange')"
      >
        {{ getLabelWithDefaultUnit(Parameter.Potassium) }}: {{ formatRange(potassiumRange) }}
      </VChip>

      <VChip
        v-if="hasRangeFilter(hemoglobinRange)"
        closable
        @click:close="clearRange('hemoglobinRange')"
      >
        {{ getLabelWithDefaultUnit(Parameter.Hemoglobin) }}: {{ formatRange(hemoglobinRange) }}
      </VChip>

      <VChip
        v-if="hasRangeFilter(heartRateRange)"
        closable
        @click:close="clearRange('heartRateRange')"
      >
        {{ getLabelWithDefaultUnit(Parameter.HeartRate) }}: {{ formatRange(heartRateRange) }}
      </VChip>

      <VChip
        v-if="hasRangeFilter(oxygenSaturationRange)"
        closable
        @click:close="clearRange('oxygenSaturationRange')"
      >
        {{ getLabelWithDefaultUnit(Parameter.OxygenSaturation) }}:
        {{ formatRange(oxygenSaturationRange) }}
      </VChip>
    </div>

    <VExpansionPanels>
      <VExpansionPanel>
        <VExpansionPanelTitle>{{
          getLabelWithDefaultUnit(Parameter.Temperature)
        }}</VExpansionPanelTitle>
        <VExpansionPanelText>
          <MortalityRangeSlider
            v-model="temperatureRange"
            :min="32"
            :max="42"
            :step="0.1"
            :custom-ticks="[32, 42]"
          />
        </VExpansionPanelText>
      </VExpansionPanel>

      <VExpansionPanel>
        <VExpansionPanelTitle>{{
          getLabelWithDefaultUnit(Parameter.Lactate)
        }}</VExpansionPanelTitle>
        <VExpansionPanelText>
          <MortalityRangeSlider
            v-model="lactateRange"
            :min="0.5"
            :max="15"
            :step="0.5"
            :custom-ticks="[0.5, 15]"
          />
        </VExpansionPanelText>
      </VExpansionPanel>

      <VExpansionPanel>
        <VExpansionPanelTitle>{{ getParameterLabel(Parameter.PH) }}</VExpansionPanelTitle>
        <VExpansionPanelText>
          <MortalityRangeSlider
            v-model="pHRange"
            :min="6.8"
            :max="8.2"
            :step="0.1"
            :custom-ticks="[6.8, 8.2]"
          />
        </VExpansionPanelText>
      </VExpansionPanel>

      <VExpansionPanel>
        <VExpansionPanelTitle>{{
          getLabelWithDefaultUnit(Parameter.Potassium)
        }}</VExpansionPanelTitle>
        <VExpansionPanelText>
          <MortalityRangeSlider
            v-model="potassiumRange"
            :min="1.5"
            :max="8"
            :step="0.1"
            :custom-ticks="[1.5, 8]"
          />
        </VExpansionPanelText>
      </VExpansionPanel>

      <VExpansionPanel>
        <VExpansionPanelTitle>{{
          getLabelWithDefaultUnit(Parameter.Hemoglobin)
        }}</VExpansionPanelTitle>
        <VExpansionPanelText>
          <MortalityRangeSlider
            v-model="hemoglobinRange"
            :min="3"
            :max="18"
            :step="0.5"
            :custom-ticks="[3, 18]"
          />
        </VExpansionPanelText>
      </VExpansionPanel>

      <VExpansionPanel>
        <VExpansionPanelTitle>{{
          getLabelWithDefaultUnit(Parameter.HeartRate)
        }}</VExpansionPanelTitle>
        <VExpansionPanelText>
          <MortalityRangeSlider
            v-model="heartRateRange"
            :min="20"
            :max="180"
            :step="1"
            :custom-ticks="[20, 180]"
          />
        </VExpansionPanelText>
      </VExpansionPanel>

      <VExpansionPanel>
        <VExpansionPanelTitle>{{
          getLabelWithDefaultUnit(Parameter.OxygenSaturation)
        }}</VExpansionPanelTitle>
        <VExpansionPanelText>
          <MortalityRangeSlider
            v-model="oxygenSaturationRange"
            :min="70"
            :max="100"
            :step="1"
            :custom-ticks="[70, 100]"
          />
        </VExpansionPanelText>
      </VExpansionPanel>
    </VExpansionPanels>
  </VCardItem>

  <VCardItem class="px-8 py-0">
    <span class="text-subtitle-1">
      {{ t('Organ Support') }}
    </span>
    <div class="flex flex-col text-body-1">
      <VCheckbox class="m-0" v-model="vasopressors" :label="t('Vasopressors')" hide-details />
      <VCheckbox v-model="dialysis" :label="t('Dialysis')" hide-details />
      <VCheckbox v-model="ventilation" :label="t('Ventilation')" hide-details />
      <VCheckbox v-model="ecmo" :label="t('ECMO')" hide-details />
      <VCheckbox v-model="impella" :label="t('Impella')" hide-details />
    </div>
  </VCardItem>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { storeToRefs } from 'pinia'
import { computed, onMounted } from 'vue'
import type { DepartmentDTO, HospitalDTO, WardDTO } from '@/generated'
import MortalityRangeSlider from './MortalityRangeSlider.vue'
import { usePatientsWithVitalSignsStore } from '@/stores/patientsWithVitalSigns'
import type { SelectOption } from '@/types/SelectOption'
import useParameterLabel from '@/composables/useParameterLabel'
import { Parameter } from '@/enums/Parameter'

const emits = defineEmits(['close-dialog', 'apply-filter'])

const { t } = useI18n()
const { getLabelWithDefaultUnit, getParameterLabel } = useParameterLabel()
const patientsWithVitalSignsStore = usePatientsWithVitalSignsStore()
const { vitalSignsFilters, filterOptions } = storeToRefs(patientsWithVitalSignsStore)

onMounted(() => patientsWithVitalSignsStore.fetchFilterOptions())

function compareById(a: { id: string } | null, b: { id: string } | null): boolean {
  return a?.id === b?.id
}

const hospital = computed({
  get: () => vitalSignsFilters.value.hospitals || [],
  set: (value) => {
    vitalSignsFilters.value.hospitals = value
  }
})

const wards = computed({
  get: () => vitalSignsFilters.value.wards || [],
  set: (value) => {
    vitalSignsFilters.value.wards = value
  }
})

const departments = computed({
  get: () => vitalSignsFilters.value.departments || [],
  set: (value) => {
    vitalSignsFilters.value.departments = value
  }
})

const temperatureRange = computed({
  get: () => vitalSignsFilters.value.temperatureRange?.map(Number) || [],
  set: (value) => {
    vitalSignsFilters.value.temperatureRange = value
  }
})

const lactateRange = computed({
  get: () => vitalSignsFilters.value.lactateRange?.map(Number) || [],
  set: (value) => {
    vitalSignsFilters.value.lactateRange = value
  }
})

const pHRange = computed({
  get: () => vitalSignsFilters.value.pHRange?.map(Number) || [],
  set: (value) => {
    vitalSignsFilters.value.pHRange = value
  }
})

const potassiumRange = computed({
  get: () => vitalSignsFilters.value.potassiumRange?.map(Number) || [],
  set: (value) => {
    vitalSignsFilters.value.potassiumRange = value
  }
})

const hemoglobinRange = computed({
  get: () => vitalSignsFilters.value.hemoglobinRange?.map(Number) || [],
  set: (value) => {
    vitalSignsFilters.value.hemoglobinRange = value
  }
})

const heartRateRange = computed({
  get: () => vitalSignsFilters.value.heartRateRange?.map(Number) || [],
  set: (value) => {
    vitalSignsFilters.value.heartRateRange = value
  }
})

const oxygenSaturationRange = computed({
  get: () => vitalSignsFilters.value.oxygenSaturationRange?.map(Number) || [],
  set: (value) => {
    vitalSignsFilters.value.oxygenSaturationRange = value
  }
})

const vasopressors = computed({
  get: () => vitalSignsFilters.value.vasopressors || false,
  set: (value) => {
    vitalSignsFilters.value.vasopressors = value || undefined
  }
})

const dialysis = computed({
  get: () => vitalSignsFilters.value.dialysis || false,
  set: (value) => {
    vitalSignsFilters.value.dialysis = value || undefined
  }
})

const ventilation = computed({
  get: () => vitalSignsFilters.value.ventilation || false,
  set: (value) => {
    vitalSignsFilters.value.ventilation = value || undefined
  }
})

const ecmo = computed({
  get: () => vitalSignsFilters.value.ecmo || false,
  set: (value) => {
    vitalSignsFilters.value.ecmo = value || undefined
  }
})

const impella = computed({
  get: () => vitalSignsFilters.value.impella || false,
  set: (value) => {
    vitalSignsFilters.value.impella = value || undefined
  }
})

const hospitalData = computed<SelectOption<HospitalDTO>[]>(() =>
  filterOptions.value.hospitals.map((option) => ({
    value: option.hospital,
    title: `${option.hospital.name} (${option.patientCount})`
  }))
)

const wardDataWithPatientCount = computed<SelectOption<WardDTO>[]>(() =>
  filterOptions.value.wards.map((option) => ({
    value: option.ward,
    title: `${option.ward.name} (${option.patientCount})`
  }))
)

const departmentDataWithPatientCount = computed<SelectOption<DepartmentDTO>[]>(() =>
  filterOptions.value.departments.map((option) => ({
    value: option.department,
    title: `${t(option.department.name)} (${option.patientCount})`
  }))
)

function hasRangeFilter(range: readonly (string | number)[] | undefined): boolean {
  return Array.isArray(range) && range.length === 2 && range.every((value) => value !== undefined)
}

function formatRange(range: readonly (string | number)[] | undefined): string {
  if (!range || !hasRangeFilter(range)) return ''
  return `${range[0]} - ${range[1]}`
}

function clearRange(
  rangeKey:
    | 'temperatureRange'
    | 'lactateRange'
    | 'pHRange'
    | 'potassiumRange'
    | 'hemoglobinRange'
    | 'heartRateRange'
    | 'oxygenSaturationRange'
) {
  if (vitalSignsFilters.value) {
    switch (rangeKey) {
      case 'temperatureRange':
        vitalSignsFilters.value.temperatureRange = []
        break
      case 'lactateRange':
        vitalSignsFilters.value.lactateRange = []
        break
      case 'pHRange':
        vitalSignsFilters.value.pHRange = []
        break
      case 'potassiumRange':
        vitalSignsFilters.value.potassiumRange = []
        break
      case 'hemoglobinRange':
        vitalSignsFilters.value.hemoglobinRange = []
        break
      case 'heartRateRange':
        vitalSignsFilters.value.heartRateRange = []
        break
      case 'oxygenSaturationRange':
        vitalSignsFilters.value.oxygenSaturationRange = []
        break
    }
  }
}
</script>
